// ════════════════════════════════════════════════════════════════════════════
// Onglet « Dispatch » — refonte UX glisser-déposer (mobile-first, ludique)
// ────────────────────────────────────────────────────────────────────────────
// Le responsable répartit son équipe disponible sur SES spots, jour par jour :
//   • Glisser-déposer tactile (poignée) : collaborateur → spot (placer / déplacer)
//   • Glisser un membre placé vers la zone « Retirer » = désaffecter
//   • Repli « taper pour placer » (Drawer) pour une fiabilité totale sur mobile
//   • Édition rapide des heures (créneaux presets + personnalisé)
//
// Placements MANUELS (auto_dispo=False) → jamais écrasés par l'auto-dispatch.
// Tout se synchronise AUTOMATIQUEMENT vers le planning back-office (affectations).
// Charte Shariti stricte (orange #EE722E, surfaces crème, Montserrat).
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";
import { RecruiterAvatar, rdPhotoSrc } from "@/components/RecruiterAvatar";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, useDraggable, useDroppable, closestCorners,
} from "@dnd-kit/core";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose,
} from "@/components/ui/drawer";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import {
  Users, Clock, MapPin, ChevronLeft, ChevronRight, Loader2, Check, CheckCircle2,
  GripVertical, Trash2, Plus, Wand2, Lock, TrendingUp, RefreshCw, CalendarCheck2,
  PartyPopper, Hand, CopyPlus,
} from "lucide-react";

// Créneaux rapides proposés dans l'éditeur d'heures.
const DISPATCH_SLOTS = [
  { label: "10h–14h", cd: "10:00", cf: "14:00" },
  { label: "10h–18h", cd: "10:00", cf: "18:00" },
  { label: "10h–20h", cd: "10:00", cf: "20:00" },
  { label: "15h–20h", cd: "15:00", cf: "20:00" },
  { label: "16h–20h", cd: "16:00", cf: "20:00" },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Initiales pour l'avatar (1 à 2 lettres).
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const hhmm = (v) => /^([01]?\d|2[0-3]):[0-5]\d$/.test(String(v || "").trim());

// Mini-indicateur de perf (aide au choix) : bulletins qui comptent sur 30 jours.
function PerfPill({ n }) {
  const v = Number(n || 0);
  const tone = v >= 20 ? "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]"
    : v >= 8 ? "bg-[rgba(238,114,46,0.14)] text-[#9A4412]"
    : "bg-[var(--surface-2)] text-muted-ink";
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${tone}`}
      title={`${v} bulletin${v > 1 ? "s" : ""} sur les 30 derniers jours (aide au choix)`}>
      <TrendingUp className="h-2.5 w-2.5" /> {v}
    </span>
  );
}

function IndepBadge({ population }) {
  if (population !== "independant") return null;
  return <span className="rounded bg-[rgba(93,63,211,0.12)] px-1 text-[8px] font-bold uppercase tracking-wide text-[#5D3FD3]">Indé</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// Carte collaborateur du POOL « À placer » (draggable via poignée + tap-to-assign).
// ────────────────────────────────────────────────────────────────────────────
function PoolChip({ member, canEdit, onTap, dimmed }) {
  const dragId = `pool:${member.rd_id}`;
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: dragId,
    disabled: !canEdit,
    data: { kind: "pool", rd_id: member.rd_id, member },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid="dispatch-pool-collaborator-item"
      className={`group flex w-full items-stretch gap-2 rounded-[14px] border border-[var(--border-soft)] bg-white px-2 py-2 shadow-[var(--shadow-sm)] transition-[box-shadow,border-color,opacity] duration-200 ${dimmed ? "opacity-40" : "opacity-100"} ${canEdit ? "hover:border-[rgba(238,114,46,0.28)] hover:shadow-[var(--shadow-md)]" : ""}`}
    >
      {canEdit && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Déplacer ${member.name}`}
          data-testid="dispatch-collaborator-drag-handle"
          className="grid w-8 shrink-0 cursor-grab touch-none place-items-center rounded-lg bg-[rgba(24,24,24,0.05)] text-muted-ink transition-colors hover:bg-[rgba(238,114,46,0.14)] hover:text-[var(--shariti-orange)] active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <RecruiterAvatar name={member.name} src={rdPhotoSrc(member)} size={38} className="self-center" testid="dispatch-pool-avatar" />
      <button
        type="button"
        onClick={() => canEdit && onTap(member)}
        disabled={!canEdit}
        data-testid="dispatch-pool-collaborator-tap-assign-button"
        className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 text-left disabled:cursor-default"
      >
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[13px] font-bold leading-tight text-ink [overflow-wrap:anywhere]">{member.name}</span>
          <IndepBadge population={member.population} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <PerfPill n={member.perf} />
          {member.dispo
            ? <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-muted-ink"><Clock className="h-2.5 w-2.5" />{member.dispo.debut}–{member.dispo.fin}</span>
            : <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-ink">hors dispo</span>}
        </div>
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Ligne d'un collaborateur PLACÉ sur un spot (drag pour déplacer + heures + retirer).
// ────────────────────────────────────────────────────────────────────────────
function AssignedRow({ member, bookingId, canEdit, onEditHours, onRemove, dimmed }) {
  const dragId = `aff:${member.affectation_id}`;
  const locked = member.pointe || !canEdit;
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: dragId,
    disabled: locked,
    data: {
      kind: "placed", rd_id: member.rd_id, affectation_id: member.affectation_id,
      booking_id: bookingId, cd: member.creneau_debut, cf: member.creneau_fin, member,
    },
  });
  return (
    <motion.div
      ref={setNodeRef}
      layout={!prefersReducedMotion()}
      initial={prefersReducedMotion() ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: dimmed ? 0.4 : 1, y: 0 }}
      exit={prefersReducedMotion() ? undefined : { opacity: 0, y: -4 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      data-testid="dispatch-assigned-member-row"
      className="flex items-start gap-2 rounded-[12px] border border-[var(--border-soft)] bg-white px-2 py-1.5 shadow-[var(--shadow-sm)]"
    >
      <RecruiterAvatar name={member.rd_name} src={rdPhotoSrc(member)} size={34} className="mt-0.5" testid="dispatch-assigned-avatar" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[12px] font-bold leading-tight text-ink [overflow-wrap:anywhere]">{member.rd_name}</span>
          <IndepBadge population={member.population} />
          {member.dispatched_by === "resp" ? (
            <span className="rounded bg-[rgba(238,114,46,0.14)] px-1 text-[8px] font-bold uppercase tracking-wide text-[#9A4412]" title="Placé par vous">Vous</span>
          ) : member.auto_dispo ? (
            <span className="rounded bg-[var(--surface-2)] px-1 text-[8px] font-bold uppercase tracking-wide text-muted-ink" title="Placement automatique">Auto</span>
          ) : null}
          {member.pointe && (
            <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(31,157,85,0.12)] px-1 py-0.5 text-[9px] font-bold uppercase text-[#0F5A2F]"><Check className="h-2.5 w-2.5" />Pointé</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {member.code_rd && <span className="font-mono text-[10px] font-bold text-[var(--shariti-orange)]">{member.code_rd}</span>}
          <PerfPill n={member.perf} />
          <button
            type="button"
            onClick={() => !locked && onEditHours(member)}
            disabled={locked}
            data-testid="dispatch-assigned-hours-button"
            className={`inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-[rgba(255,243,234,0.7)] px-2 py-0.5 text-[11px] font-bold text-ink transition-colors ${locked ? "opacity-70" : "hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.1)] active:scale-[0.98]"}`}
          >
            <Clock className="h-3 w-3 text-[var(--shariti-orange)]" />
            {member.creneau_debut}–{member.creneau_fin}
          </button>
        </div>
      </div>

      {locked ? (
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center text-muted-ink" title="Déjà pointé — gérez les heures dans l'onglet Heures"><Lock className="h-3.5 w-3.5" /></span>
      ) : (
        <div className="mt-0.5 flex shrink-0 items-center gap-1">
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={`Déplacer ${member.rd_name} vers un autre spot`}
            data-testid="dispatch-assigned-drag-handle"
            className="grid h-9 w-8 cursor-grab touch-none place-items-center rounded-lg bg-[rgba(24,24,24,0.05)] text-muted-ink transition-colors hover:bg-[rgba(238,114,46,0.14)] hover:text-[var(--shariti-orange)] active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(member)}
            data-testid="dispatch-assigned-remove-button"
            aria-label={`Retirer ${member.rd_name}`}
            title="Retirer du spot"
            className="grid h-9 w-9 place-items-center rounded-lg text-[#9B2226] transition-colors hover:bg-[rgba(214,69,69,0.1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C0392B]"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Carte SPOT = zone de dépôt (droppable).
// ────────────────────────────────────────────────────────────────────────────
function SpotCard({ spot, canEdit, activeKind, onEditHours, onRemove, onAdd, dimmedAffId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `spot:${spot.booking_id}`,
    disabled: !canEdit,
    data: { kind: "spot", booking_id: spot.booking_id },
  });
  const highlight = canEdit && activeKind && isOver;
  const canDrop = canEdit && activeKind;
  const count = spot.count;
  return (
    <div
      ref={setNodeRef}
      data-testid="dispatch-spot-card"
      data-over={isOver ? "true" : "false"}
      className={`rounded-[18px] border bg-white p-3 shadow-[var(--shadow-sm)] transition-[border-color,background-color,box-shadow,transform] duration-200 ${
        highlight ? "border-[rgba(238,114,46,0.55)] bg-[rgba(238,114,46,0.08)] shadow-[var(--shadow-md)] scale-[1.01]"
          : canDrop ? "border-[rgba(238,114,46,0.3)]"
          : "border-[var(--border-soft)]"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="break-words text-[13px] font-extrabold leading-tight text-ink">{spot.spot_name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-ink">
            {spot.ligne_label && <span className="font-semibold">{spot.ligne_label}</span>}
            {spot.spot_address && <span className="inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5 shrink-0" />{spot.spot_address}</span>}
          </div>
        </div>
        <span
          data-testid="dispatch-spot-count"
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold ${
            count >= 3 ? "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]"
              : count > 0 ? "bg-[rgba(238,114,46,0.14)] text-[#9A4412]"
              : "bg-[var(--surface-2)] text-muted-ink"
          }`}
        >
          {count} <Users className="ml-0.5 inline h-2.5 w-2.5" />
        </span>
      </div>

      {/* Membres placés */}
      {spot.team.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <AnimatePresence initial={false}>
            {spot.team.map((m) => (
              <AssignedRow
                key={m.affectation_id}
                member={m}
                bookingId={spot.booking_id}
                canEdit={canEdit}
                onEditHours={onEditHours}
                onRemove={onRemove}
                dimmed={dimmedAffId === m.affectation_id}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Zone de dépôt visuelle + ajout par tap */}
      {canEdit && (
        <div
          data-testid="dispatch-spot-dropzone"
          className={`mt-2 flex items-center justify-center gap-1.5 rounded-[12px] border border-dashed py-2 text-[12px] font-bold transition-colors ${
            highlight ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"
              : "border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.04)] text-[var(--shariti-orange)]"
          }`}
        >
          {activeKind ? (
            <span className="inline-flex items-center gap-1.5"><Hand className="h-4 w-4" />Déposer ici</span>
          ) : (
            <button
              type="button"
              onClick={() => onAdd(spot)}
              data-testid="dispatch-spot-add-member-button"
              className="inline-flex w-full items-center justify-center gap-1.5 py-0.5"
            >
              <Plus className="h-4 w-4" /> Ajouter un collaborateur
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Zone POOL = droppable « Retirer » quand on glisse un membre déjà placé.
// ────────────────────────────────────────────────────────────────────────────
function PoolDropWrapper({ children, canEdit, activePlaced }) {
  const { setNodeRef, isOver } = useDroppable({
    id: "__pool__",
    disabled: !canEdit || !activePlaced,
    data: { kind: "pool-zone" },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid="dispatch-pool-section"
      data-over={isOver ? "true" : "false"}
      className={`rounded-[18px] border p-3 transition-colors duration-200 ${
        activePlaced && isOver ? "border-[rgba(192,57,43,0.5)] bg-[rgba(192,57,43,0.06)]"
          : activePlaced ? "border-dashed border-[rgba(192,57,43,0.35)] bg-white"
          : "border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.05)]"
      }`}
    >
      {activePlaced && (
        <div className="mb-2 flex items-center justify-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[#9B2226]">
          <Trash2 className="h-3.5 w-3.5" /> Relâcher ici pour retirer
        </div>
      )}
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Éditeur d'heures (Drawer) : presets + personnalisé.
// ════════════════════════════════════════════════════════════════════════════
function HoursDrawer({ target, busy, onClose, onSave }) {
  const [cd, setCd] = useState("10:00");
  const [cf, setCf] = useState("14:00");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (target) {
      setCd(target.creneau_debut || "10:00");
      setCf(target.creneau_fin || "14:00");
      setErr("");
    }
  }, [target]);

  const activePreset = DISPATCH_SLOTS.find((s) => s.cd === cd && s.cf === cf)?.label || "";

  const submit = () => {
    if (!hhmm(cd) || !hhmm(cf)) { setErr("Format attendu HH:MM (ex. 10:00)."); return; }
    if (cd >= cf) { setErr("L'heure de début doit précéder l'heure de fin."); return; }
    onSave(target, cd, cf);
  };

  return (
    <Drawer open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent data-testid="dispatch-hours-editor-drawer">
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-base">Heures · {target?.rd_name}</DrawerTitle>
            <DrawerDescription className="text-xs">Choisissez un créneau rapide ou ajustez manuellement.</DrawerDescription>
          </DrawerHeader>

          <div className="px-4 pb-2">
            <ToggleGroup
              type="single"
              value={activePreset}
              onValueChange={(val) => {
                const s = DISPATCH_SLOTS.find((x) => x.label === val);
                if (s) { setCd(s.cd); setCf(s.cf); setErr(""); }
              }}
              className="grid grid-cols-3 gap-2"
            >
              {DISPATCH_SLOTS.map((s) => (
                <ToggleGroupItem
                  key={s.label}
                  value={s.label}
                  data-testid="dispatch-hours-preset-toggle"
                  className="rounded-[12px] border border-[var(--border-soft)] bg-white py-2 text-[12px] font-bold data-[state=on]:border-[var(--shariti-orange)] data-[state=on]:bg-[rgba(238,114,46,0.12)] data-[state=on]:text-[var(--shariti-orange)]"
                >
                  {s.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <label className="text-[11px] font-semibold text-muted-ink">
                Début
                <Input
                  type="time"
                  value={cd}
                  onChange={(e) => { setCd(e.target.value); setErr(""); }}
                  data-testid="dispatch-hours-custom-start-input"
                  className="mt-1 h-11 text-sm"
                />
              </label>
              <label className="text-[11px] font-semibold text-muted-ink">
                Fin
                <Input
                  type="time"
                  value={cf}
                  onChange={(e) => { setCf(e.target.value); setErr(""); }}
                  data-testid="dispatch-hours-custom-end-input"
                  className="mt-1 h-11 text-sm"
                />
              </label>
            </div>

            {err && (
              <p data-testid="dispatch-hours-error" className="mt-2 rounded-lg bg-[rgba(214,69,69,0.1)] px-2.5 py-1.5 text-[11px] font-semibold text-[#9B2226]">{err}</p>
            )}
          </div>

          <DrawerFooter>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="dispatch-hours-save-button"
              className="tap-target inline-flex w-full items-center justify-center gap-2 rounded-[12px] bg-[var(--shariti-orange)] px-4 text-sm font-extrabold text-white shadow-[var(--shadow-sm)] transition-colors hover:bg-[#E56622] active:scale-[0.99] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer les heures
            </button>
            <DrawerClose asChild>
              <button type="button" className="tap-target w-full rounded-[12px] border border-[var(--border-soft)] bg-white text-sm font-semibold text-muted-ink">Annuler</button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Drawer « taper pour placer » (fallback) : liste des spots pour un collaborateur.
// ════════════════════════════════════════════════════════════════════════════
function TapAssignDrawer({ target, spots, busy, onClose, onPick }) {
  return (
    <Drawer open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent data-testid="dispatch-tap-assign-drawer">
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-base [overflow-wrap:anywhere]">Placer {target?.name}</DrawerTitle>
            <DrawerDescription className="text-xs">Choisissez le spot sur lequel positionner ce collaborateur.</DrawerDescription>
          </DrawerHeader>
          <div className="max-h-[55vh] space-y-1.5 overflow-y-auto px-4 pb-4">
            {spots.length === 0 && <p className="py-6 text-center text-sm text-muted-ink">Aucun spot disponible ce jour.</p>}
            {spots.map((s) => (
              <button
                key={s.booking_id}
                type="button"
                disabled={busy}
                onClick={() => onPick(s.booking_id)}
                data-testid="dispatch-tap-assign-spot-option"
                className="flex w-full items-center justify-between gap-2 rounded-[12px] border border-[var(--border-soft)] bg-white px-3 py-3 text-left transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.05)] disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block text-[13px] font-bold text-ink [overflow-wrap:anywhere]">{s.spot_name}</span>
                  {s.ligne_label && <span className="block text-[10px] text-muted-ink [overflow-wrap:anywhere]">{s.ligne_label}</span>}
                </span>
                <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-extrabold text-muted-ink">{s.count} <Users className="ml-0.5 inline h-2.5 w-2.5" /></span>
              </button>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Drawer « ajouter sur un spot » (fallback) : liste des collaborateurs à placer.
// ════════════════════════════════════════════════════════════════════════════
function AddMemberDrawer({ target, pool, busy, onClose, onPick }) {
  return (
    <Drawer open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent data-testid="dispatch-add-member-drawer">
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-base [overflow-wrap:anywhere]">Ajouter sur {target?.spot_name}</DrawerTitle>
            <DrawerDescription className="text-xs">Collaborateurs de votre équipe à positionner.</DrawerDescription>
          </DrawerHeader>
          <div className="max-h-[55vh] space-y-1.5 overflow-y-auto px-4 pb-4">
            {pool.length === 0 && <p className="py-6 text-center text-sm text-muted-ink">Aucun collaborateur à placer.</p>}
            {pool.map((m) => (
              <button
                key={m.rd_id}
                type="button"
                disabled={busy}
                onClick={() => onPick(m.rd_id)}
                data-testid="dispatch-add-member-option"
                className="flex w-full items-center justify-between gap-2 rounded-[12px] border border-[var(--border-soft)] bg-white px-3 py-3 text-left transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.05)] disabled:opacity-50"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <RecruiterAvatar name={m.name} src={rdPhotoSrc(m)} size={34} />
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-1"><span className="text-[13px] font-bold text-ink [overflow-wrap:anywhere]">{m.name}</span><IndepBadge population={m.population} /></span>
                    <span className="mt-0.5 flex items-center gap-1.5"><PerfPill n={m.perf} />{m.dispo ? <span className="text-[10px] text-muted-ink">{m.dispo.debut}–{m.dispo.fin}</span> : <span className="text-[9px] uppercase text-muted-ink">hors dispo</span>}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════════════════
export function RespDispatch({ token, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);     // { kind, rd_id, member, affectation_id?, booking_id?, cd?, cf? }
  const [hoursTarget, setHoursTarget] = useState(null);    // membre placé → éditer heures
  const [tapTarget, setTapTarget] = useState(null);        // { rd_id, name } → placer via Drawer
  const [addTarget, setAddTarget] = useState(null);        // spot → ajouter via Drawer
  const [showAutres, setShowAutres] = useState(false);
  const prevToPlace = useRef(0);
  const didDragRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const q = d ? `?date=${d}` : "";
      const r = await publicApi.get(`/pointage/resp/${token}/dispatch${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch indisponible.");
      setData(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const canEdit = !!(data && data.editable && !readOnly);
  const spots = data?.spots || [];
  const aPlacer = useMemo(() => data?.a_placer || [], [data]);
  const autres = useMemo(() => data?.autres || [], [data]);

  // Confetti discret quand TOUT est placé (une seule fois, respect reduced-motion).
  useEffect(() => {
    if (!data) return;
    const toPlace = aPlacer.length;
    const hasPlaced = spots.some((s) => s.team.length > 0);
    if (prevToPlace.current > 0 && toPlace === 0 && hasPlaced && !prefersReducedMotion()) {
      try {
        confetti({ particleCount: 70, spread: 60, startVelocity: 32, scalar: 0.8, origin: { y: 0.3 }, ticks: 140,
          colors: ["#EE722E", "#F89F63", "#1F9D55", "#FFF3EA"] });
      } catch (_) { /* no-op */ }
    }
    prevToPlace.current = toPlace;
  }, [data, aPlacer.length, spots]);

  const frLong = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); } catch { return iso; } };

  const shiftDay = (delta) => {
    if (!data?.date) return;
    const dd = new Date(data.date + "T12:00:00");
    dd.setDate(dd.getDate() + delta);
    const iso = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}-${String(dd.getDate()).padStart(2, "0")}`;
    load(iso);
  };

  const applyBoard = (board) => { if (board) setData(board); };

  const assign = async (rd_id, booking_id, cd = null, cf = null) => {
    setBusy(true);
    try {
      const body = { rd_id, booking_id };
      if (cd && cf) { body.creneau_debut = cd; body.creneau_fin = cf; }
      const r = await publicApi.post(`/pointage/resp/${token}/dispatch/assign`, body);
      applyBoard(r.data.board);
      setTapTarget(null); setAddTarget(null);
      toast.success(r.data.moved ? "Collaborateur déplacé" : "Déposé sur le spot");
    } catch (e) { toast.error(e?.response?.data?.detail || "Placement impossible."); }
    finally { setBusy(false); }
  };

  const unassign = async (affectation_id) => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/pointage/resp/${token}/dispatch/unassign`, { affectation_id });
      applyBoard(r.data.board);
      toast.success("Retiré du spot");
    } catch (e) { toast.error(e?.response?.data?.detail || "Retrait impossible."); }
    finally { setBusy(false); }
  };

  const saveHours = async (member, cd, cf) => {
    setBusy(true);
    try {
      const r = await publicApi.put(`/pointage/resp/${token}/dispatch/creneau`, {
        affectation_id: member.affectation_id, creneau_debut: cd, creneau_fin: cf,
      });
      applyBoard(r.data.board);
      setHoursTarget(null);
      toast.success("Heures mises à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Modification impossible."); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    if (!data?.date) return;
    setBusy(true);
    try {
      const r = await publicApi.post(`/pointage/resp/${token}/dispatch/complete`, { date: data.date });
      applyBoard(r.data.board);
      toast.success("Disponibles restants placés automatiquement");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible."); }
    finally { setBusy(false); }
  };

  const frShort = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }); } catch { return iso; } };

  const copyNextDay = async () => {
    if (!data?.date) return;
    setBusy(true);
    try {
      const r = await publicApi.post(`/pointage/resp/${token}/dispatch/copy-next-day`, { date: data.date });
      const { copied, skipped_no_spot, target_date, board, message } = r.data;
      applyBoard(board); // le board renvoyé concerne le LENDEMAIN → la vue bascule dessus
      if (copied > 0) {
        const extra = skipped_no_spot > 0 ? ` · ${skipped_no_spot} sans spot` : "";
        toast.success(`${copied} placement${copied > 1 ? "s" : ""} copié${copied > 1 ? "s" : ""} vers ${frShort(target_date)}${extra}`);
      } else {
        toast.info(message || "Aucun spot correspondant le lendemain.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Copie impossible."); }
    finally { setBusy(false); }
  };

  // ── Drag & drop ────────────────────────────────────────────────────────────
  const handleDragStart = (event) => {
    didDragRef.current = true;
    setActiveDrag(event.active.data.current || null);
  };
  const handleDragCancel = () => { setActiveDrag(null); setTimeout(() => { didDragRef.current = false; }, 50); };
  const handleDragEnd = (event) => {
    const active = event.active?.data?.current;
    const overId = event.over?.id;
    setActiveDrag(null);
    setTimeout(() => { didDragRef.current = false; }, 50);
    if (!active || !overId) return;
    if (typeof overId === "string" && overId.startsWith("spot:")) {
      const booking_id = overId.slice(5);
      if (active.kind === "placed" && active.booking_id === booking_id) return; // déjà là
      // On conserve les heures du membre déplacé (sinon dispo/défaut côté backend).
      assign(active.rd_id, booking_id, active.cd || null, active.cf || null);
    } else if (overId === "__pool__" && active.kind === "placed") {
      unassign(active.affectation_id);
    }
  };

  const activeKind = activeDrag?.kind || null;
  const activePlaced = activeKind === "placed";
  const dimmedPoolId = activeKind === "pool" ? activeDrag?.rd_id : null;
  const dimmedAffId = activeKind === "placed" ? activeDrag?.affectation_id : null;

  // Nom affiché dans l'overlay de drag.
  const overlayName = activeDrag?.member?.name || activeDrag?.member?.rd_name || "";
  const overlaySrc = rdPhotoSrc(activeDrag?.member);

  return (
    <div className="space-y-3" data-testid="resp-dispatch-tab">
      {/* ── Header : navigation jour + synchro ───────────────────────────── */}
      <div className="rounded-[18px] border border-[var(--border-soft)] bg-white p-2 shadow-[var(--shadow-sm)]">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => shiftDay(-1)} disabled={loading || busy} data-testid="dispatch-prev-day-button"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
          <div className="min-w-0 flex-1 text-center">
            <div className="truncate text-sm font-extrabold capitalize text-ink" data-testid="dispatch-date">{data?.date ? frLong(data.date) : "—"}</div>
            <div className="truncate text-[11px] text-muted-ink tabular-nums">{spots.length} spot{spots.length > 1 ? "s" : ""} · {aPlacer.length} à placer</div>
          </div>
          <button onClick={() => shiftDay(1)} disabled={loading || busy} data-testid="dispatch-next-day-button"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-1.5">
            <button onClick={() => load()} disabled={loading || busy} data-testid="dispatch-today-button"
              className="rounded-lg px-2 py-1 text-xs font-bold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.08)] disabled:opacity-50">Aujourd'hui</button>
            {canEdit && spots.some((s) => s.team.length > 0) && (
              <button onClick={copyNextDay} disabled={loading || busy} data-testid="dispatch-copy-next-day-button"
                title="Copier ces placements vers le lendemain"
                className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-2 py-1 text-xs font-bold text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)] disabled:opacity-50">
                <CopyPlus className="h-3.5 w-3.5" /> Copier → demain
              </button>
            )}
          </div>
          <span data-testid="dispatch-sync-status" className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-2 py-1 text-[10px] font-semibold text-muted-ink shadow-[var(--shadow-sm)]">
            {busy ? (<><Loader2 className="h-3 w-3 animate-spin text-[var(--shariti-orange)]" /> Synchronisation…</>)
              : (<><CheckCircle2 className="h-3 w-3 text-[#1F9D55]" /> Synchronisé au planning</>)}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="dispatch-loading">
          <Skeleton className="h-20 w-full rounded-[18px]" />
          <Skeleton className="h-28 w-full rounded-[18px]" />
          <Skeleton className="h-28 w-full rounded-[18px]" />
        </div>
      ) : !data ? null : (
        <DndContext sensors={sensors} collisionDetection={closestCorners}
          onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>

          {!canEdit && (
            <div className="flex items-start gap-2 rounded-[14px] border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid="dispatch-readonly-banner">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-ink" />
              <p className="text-[11px] leading-relaxed text-muted-ink">
                {data.is_past ? "Journée passée — le dispatch est verrouillé (consultation seule)." : "Consultation seule."}
              </p>
            </div>
          )}

          {/* ── POOL « À placer » (droppable « Retirer » pendant un drag de membre placé) ── */}
          <PoolDropWrapper canEdit={canEdit} activePlaced={activePlaced}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]">
                <Users className="h-3.5 w-3.5" /> À placer · {aPlacer.length}
              </span>
              {canEdit && aPlacer.length > 0 && spots.length > 0 && (
                <button type="button" onClick={complete} disabled={busy} data-testid="dispatch-auto-assign-button"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--shariti-orange)] px-3 py-1.5 text-[11px] font-extrabold text-white shadow-[var(--shadow-sm)] transition-colors hover:bg-[#E56622] active:scale-[0.99] disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Répartir auto
                </button>
              )}
            </div>

            {aPlacer.length === 0 ? (
              <div className="flex flex-col items-center gap-1 rounded-[12px] bg-white/70 px-3 py-4 text-center" data-testid="dispatch-empty-pool">
                <PartyPopper className="h-6 w-6 text-[var(--shariti-orange)]" />
                <p className="text-[13px] font-extrabold text-ink">Tout le monde est placé</p>
                <p className="text-[11px] text-muted-ink">Vous pouvez passer au jour suivant.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2" data-testid="dispatch-pool-grid">
                {aPlacer.map((m) => (
                  <PoolChip key={m.rd_id} member={m} canEdit={canEdit}
                    onTap={(mm) => { if (!didDragRef.current) setTapTarget({ rd_id: mm.rd_id, name: mm.name }); }}
                    dimmed={dimmedPoolId === m.rd_id} />
                ))}
              </div>
            )}

            {/* Autres membres (sans dispo ce jour) */}
            {autres.length > 0 && (
              <div className="mt-2">
                <button type="button" onClick={() => setShowAutres((v) => !v)} data-testid="dispatch-toggle-autres"
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-muted-ink hover:text-[var(--shariti-orange)]">
                  <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAutres ? "rotate-90" : ""}`} /> Autres membres (sans dispo) · {autres.length}
                </button>
                {showAutres && (
                  <div className="mt-1.5 grid grid-cols-1 gap-2">
                    {autres.map((m) => (
                      <PoolChip key={m.rd_id} member={m} canEdit={canEdit}
                        onTap={(mm) => { if (!didDragRef.current) setTapTarget({ rd_id: mm.rd_id, name: mm.name }); }}
                        dimmed={dimmedPoolId === m.rd_id} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </PoolDropWrapper>

          {/* ── SPOTS du jour ─────────────────────────────────────────────── */}
          {spots.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="dispatch-empty-spots">
              <MapPin className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
              <h2 className="text-base font-extrabold text-ink">Aucun spot ce jour</h2>
              <p className="mt-1 text-sm text-muted-ink">Aucun emplacement n'est posé sur vos lignes pour cette journée.</p>
              <button type="button" onClick={() => shiftDay(1)} data-testid="dispatch-empty-spots-next"
                className="mt-3 inline-flex items-center gap-1.5 rounded-[12px] border border-[var(--border-soft)] bg-white px-3 py-2 text-sm font-bold text-[var(--shariti-orange)] shadow-[var(--shadow-sm)]">
                <CalendarCheck2 className="h-4 w-4" /> Voir le jour suivant
              </button>
            </div>
          ) : (
            <div className="mt-3 space-y-2.5">
              {spots.map((s) => (
                <SpotCard key={s.booking_id} spot={s} canEdit={canEdit} activeKind={activeKind}
                  onEditHours={(m) => setHoursTarget(m)}
                  onRemove={(m) => unassign(m.affectation_id)}
                  onAdd={(spot) => setAddTarget({ booking_id: spot.booking_id, spot_name: spot.spot_name })}
                  dimmedAffId={dimmedAffId} />
              ))}
            </div>
          )}

          <p className="mt-2 text-center text-[11px] leading-relaxed text-muted-ink" data-testid="dispatch-legal">
            Glissez un collaborateur sur un spot (ou tapez-le), ajustez ses heures : tout se synchronise automatiquement avec le planning. Vos choix restent prioritaires.
          </p>

          {/* Overlay de drag « soulevé » */}
          <DragOverlay dropAnimation={prefersReducedMotion() ? null : undefined}>
            {activeDrag ? (
              <div className="inline-flex max-w-[260px] items-center gap-2 rounded-[14px] bg-white px-3 py-2 shadow-[0_18px_60px_rgba(24,24,24,0.18)] ring-1 ring-[rgba(238,114,46,0.3)]">
                <RecruiterAvatar name={overlayName} src={overlaySrc} size={34} />
                <span className="text-[13px] font-extrabold leading-tight text-ink [overflow-wrap:anywhere]">{overlayName}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Drawers */}
      <HoursDrawer target={hoursTarget} busy={busy} onClose={() => setHoursTarget(null)} onSave={saveHours} />
      <TapAssignDrawer target={tapTarget} spots={spots} busy={busy} onClose={() => setTapTarget(null)}
        onPick={(booking_id) => tapTarget && assign(tapTarget.rd_id, booking_id)} />
      <AddMemberDrawer target={addTarget} pool={[...aPlacer, ...autres]} busy={busy} onClose={() => setAddTarget(null)}
        onPick={(rd_id) => addTarget && assign(rd_id, addTarget.booking_id)} />
    </div>
  );
}
